//================================================================
//  Tab: Terms
//================================================================

//  Purpose: This tab displays all the agreement terms

//  Example:
//    <Terms></Terms>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import LiveField from '../../../../../Components/LiveField/LiveField';

//Functions
import WriteDocument from '../../../../../Library/WriteDocument';
import DocumentListener from '../../../../../Library/DocumentListener';
import CheckInventoryRole from '../../../../../Library/CheckInventoryRole';

//Images

export default function Terms() {

  //------------------------------------------------------
  //  useContext & React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const getRoles = useContext(GetRoles);
  const setToast = useContext(SetToast);
  const params = useParams();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('pending');

  // Role checks
  const [roleStatus, setRoleStatus] = useState('read'); // read, download, create, write & admin

  // Holds the application document from 
  const [agreementDoc, setAgreementDoc] = useState();

  // Used to determine if the agreement end date is set to null
  const [noEndDate, setNoEndDate] = useState(false);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Function used to handle the Agreement End Date change
  // - If the value is null, set the noEndDate state to true
  function handleAgreementEndDateChange(check) {

    const value = check ? null : agreementDoc?.agreementenddate;

    setNoEndDate(check);

    // Update the document
    const updatedDoc = {
      'agreementenddate': value
    };

    WriteDocument('agreements', agreementDoc?.agreementid, updatedDoc, true)
      .catch((error) => {

        console.log('error', error);

        setToast({
          'type': 'error',
          'message': 'Failed to update Agreement End Date',
        });

      });

  }

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get Firestore document
  useEffect(() => {

    const agreementid = params.id;

    if (agreementid === '') return;
    if (agreementid === null) return;
    if (getRoles === undefined) return;

    // ============================================
    //  Perform role check
    // ============================================

    const roleStatus = CheckInventoryRole('agreements', getRoles, getUser);
    if (roleStatus === 'access-denied') return setPageStatus('access-denied');
    setRoleStatus(roleStatus);

    // ============================================
    //  Fetch Data
    // ============================================

    function onLoadChange(document) {

      if (document === undefined) return  setPageStatus('forbidden');

      setAgreementDoc(document);
      setNoEndDate(document?.agreementenddate === null)
      setPageStatus('onload');

    }

    function onError(error) {

      console.log(error);
      setPageStatus('error-fatal');

    }

    const unsubscribe = DocumentListener('agreements', agreementid, onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='flex flex-col w-[70%] px-[20px]'>

          {/* Agreement Start Date */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'agreementstartdate'}
            label={<label className='font-medium'>Agreement Start Date</label>}
            defaultValue={agreementDoc?.agreementstartdate}
            type={'date-string'}
          ></LiveField>

          {/* Agreement End Date */}
          <div className='FormComponent-Row'>
            <label className='font-medium'> Agreement End Date </label>

            {/* Checkbox to disable the date */}
            <div className='flex flex-row items-start px-[5px]'>
              <label className='Checkbox-Container'>
                <input
                  required
                  type='checkbox'
                  name='confirmation'
                  checked={noEndDate}
                  onChange={(e) => handleAgreementEndDateChange(e.target.checked)}
                  disabled={!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')}
                ></input>
                <span className={`Checkbox ${!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin') && 'bg-[#f8f8f8] cursor-not-allowed'}`}></span>
              </label>

              <label className='max-w-[400px]'>
                Agreement has no end date
              </label>
            </div>

            {
            
            !noEndDate && 
              
              <LiveField
                collectionid={'agreements'}
                documentid={agreementDoc?.agreementid}
                fieldpath={'agreementenddate'}
                defaultValue={agreementDoc?.agreementenddate}
                type={'date-string'}
                validation={{
                  type: 'date',
                  minDate: agreementDoc?.agreementstartdate
                }}
              ></LiveField>}
          </div>

          {/* Execution Method */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'executionmethod'}
            label={<label className='font-medium'>Execution Method</label>}
            defaultValue={agreementDoc?.executionmethod}
            type={'choice'}
            options={[
              'PoA',
              's127',
              'None',
              'Other'
            ]}
            disabled={false}
          ></LiveField>

          {/* Execution Date */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'executiondate'}
            label={<label className='font-medium'>Execution Date</label>}
            defaultValue={agreementDoc?.executiondate}
            type={'date-string'}
            disabled={false}
          ></LiveField>

          {/* Currency */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'currency'}
            label={<label className='font-medium'>Currency</label>}
            defaultValue={agreementDoc?.currency}
            type={'choice'}
            options={[
              'USD',
              'SGD',
              'GBP',
              'AUD',
              'EUR',
              'MYR',
            ]}
            disabled={false}
          ></LiveField>

          {/* Parent Contract */}
          <LiveField
            type={'lookup'}
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'parentcontract'}
            defaultValue={agreementDoc?.parentcontract?.poanumber}
            placeholder={'Search parent contract by PoA number'}
            dataFieldCollectionid={'agreements'}
            dataFieldPrimary={'poanumber'}
            dataFieldSecondary={'vendor.vendorname'}
            label={<label className='font-medium'> Parent Contract</label>}
            disabled={false}
          ></LiveField>

          {/* Lendlease Entity */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'lendleaseentity'}
            label={<label className='font-medium'>Lendlease Entity</label>}
            defaultValue={agreementDoc?.lendleaseentity}
            styleInput={{ width: '450px' }}
            type={'choice'}
            options={[
              'TBC',
              'Lendlease Asia Holdings Pte. Ltd',
              'Lendlease Building Pty Ltd',
              'Lendlease Chilled Water (Barangaroo South) Pty Limited',
              'Lendlease Construction Pty Limited',
              'Lendlease Design Build',
              'Lendlease Development Pty Ltd',
              'Lendlease Digital Asia Pte Ltd',
              'Lendlease Digital Australia Holdings Pty Limited',
              'Lendlease Digital Australia Pty Ltd',
              'Lendlease Digital Investments Pty Limited',
              'Lendlease Digital US Holdings LLC',
              'Lendlease Digital US LLC',
              'Lendlease Embedded Network (Barangaroo South) Pty Limited',
              'Lendlease Engineering Pty Ltd',
              'Lendlease Europe Limited',
              'Lendlease Europe Limited',
              'Lendlease Global Commercial Trust Management Pte Ltd',
              'Lendlease Group Services Pty Limited',
              'Lendlease ICT Services, Inc',
              'Lendlease Management Services Limited',
              'Lendlease Project Management & Construction (Shanghai) Co Ltd',
              'Lendlease Recycled Water (Barangaroo South) Pty Limited',
              'Lendlease Retirement Living Holding Pty Limited',
              'Lendlease Services Pty Limited',
              'Lendlease Singapore Pte Ltd',
              'Lendlease Technology Delivery Pty Limited',
              'Lendlease Technology Pty Ltd',
              'Podium Asset Holdings Pty Limited',
              'Podium Property Services LLC',
              'Lendlease (Elephant & Castle) Ltd',
              'Lendlease (US) Construction Inc',
              'Lendlease (US) Construction, LMB Inc',
              'Lendlease (US) Development',
              'Lendlease (US) Partnerships',
              'Lendlease (US) Services Inc',
            ]}
            disabled={false}
          ></LiveField>

          {/* Estimated Annual Contract Value */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'estannualcontractvalue'}
            label={<label className='font-medium'>Estimated Annual Contract Value</label>}
            defaultValue={agreementDoc?.estannualcontractvalue}
            type={'currency'}
            placeholder={'Enter contract value'}
            disabled={false}
          ></LiveField>

          {/* Estimated Total Contract Value */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'esttotalcontractvalue'}
            label={<label className='font-medium'>Estimated Total Contract Value</label>}
            defaultValue={agreementDoc?.esttotalcontractvalue}
            type={'currency'}
            placeholder={'Enter contract value'}
            disabled={false}
          ></LiveField>

          {/* Currency */}
          <LiveField
              type={'choice'}
              collectionid={'requests'}
              documentid={agreementDoc?.agreementid}
              fieldpath={'agreement.currency'}
              defaultValue={agreementDoc?.currency}
              label={<label className='font-medium'> Currency</label>}
              disabled={true}
              options={[
                  'AUD',
                  'GBP',
                  'SGD',
                  'USD',
                  'EUR',
                  'MYR',
                  'N/A',
              ]}
          ></LiveField>

          {/* Total Agreement Amount */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'totalagreementamount'}
            label={<label className='font-medium'>Total Agreement Amount</label>}
            defaultValue={agreementDoc?.totalagreementamount}
            type={'currency'}
            placeholder={'Enter amount'}
            disabled={false}
          ></LiveField>

          {/* Auto-Renewals */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'autorenewals'}
            label={<label className='font-medium'>Auto-Renewals</label>}
            defaultValue={agreementDoc?.autorenewals}
            type={'choice'}
            options={[
              'Yes',
              'No',
            ]}
            disabled={false}
          ></LiveField>

          {/* Notice Period to Terminate Auto-Renewal */}
          <div hidden={agreementDoc?.autorenewals !== 'Yes'}>
            <div className='gap-[5px] p-[5px]'>

              <label className='font-medium pt-[5px]'>Notice Period to Terminate Auto-Renewal</label>
              <div className='flex flex-row pt-[10px]'>

                <LiveField
                  type={'number'}
                  collectionid={'agreements'}
                  documentid={agreementDoc?.agreementid}
                  fieldpath={'noticeperiodforautorenewaltermination.term'}
                  defaultValue={agreementDoc?.noticeperiodforautorenewaltermination?.term}
                  styleInput={{ width: '150px' }}
                  placeholder={'Enter value'}
                ></LiveField>

                <LiveField
                  type={'choice'}
                  collectionid={'agreements'}
                  documentid={agreementDoc?.agreementid}
                  fieldpath={'noticeperiodforautorenewaltermination.period'}
                  defaultValue={agreementDoc?.noticeperiodforautorenewaltermination?.period}
                  options={[
                    'Days',
                    'Months',
                    'Years',
                  ]}
                  styleInput={{ width: '200px' }}
                ></LiveField>

              </div>
            </div>
          </div>

          {/* Extension Agreement */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'extensionagreement'}
            label={<label className='font-medium'>Extension Agreement</label>}
            defaultValue={agreementDoc?.extensionagreement}
            type={'choice'}
            options={[
              'Yes',
              'No',
            ]}
            disabled={false}
          ></LiveField>

          {/* Notice Period for Extension of Agreement */}
          <div hidden={agreementDoc?.extensionagreement !== 'Yes'}>
            <div className='gap-[5px] p-[5px]'>

              <label className='font-medium pt-[5px]'>Notice Period for Extension of Agreement</label>
              <div className='flex flex-row pt-[10px]'>

                <LiveField
                  type={'number'}
                  collectionid={'agreements'}
                  documentid={agreementDoc?.agreementid}
                  fieldpath={'noticeperiodforextension.term'}
                  defaultValue={agreementDoc?.noticeperiodforextension?.term}
                  styleInput={{ width: '150px' }}
                  placeholder={'Enter value'}
                ></LiveField>

                <LiveField
                  type={'choice'}
                  collectionid={'agreements'}
                  documentid={agreementDoc?.agreementid}
                  fieldpath={'noticeperiodforextension.period'}
                  defaultValue={agreementDoc?.noticeperiodforextension?.period}
                  options={[
                    'Days',
                    'Months',
                    'Years',
                  ]}
                  styleInput={{ width: '200px' }}
                ></LiveField>

              </div>
            </div>
          </div>

          {/* Negotiated Hard Savings */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'hardsavings'}
            label={<label className='font-medium'>Negotiated Hard Savings</label>}
            defaultValue={agreementDoc?.hardsavings}
            type={'text'}
            placeholder={'Enter amount'}
            disabled={false}
          ></LiveField>

          {/* Negotiated Soft Savings */}
          <LiveField
            collectionid={'agreements'}
            documentid={agreementDoc?.agreementid}
            fieldpath={'softsavings'}
            label={<label className='font-medium'>Negotiated Soft Savings</label>}
            defaultValue={agreementDoc?.softsavings}
            type={'text'}
            placeholder={'Enter amount'}
            disabled={false}
          ></LiveField>

        </div>
      }
    ></PageComponent>
  )
}
